import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, CreditCard, Send, Loader2, OctagonX, PauseCircle, PlayCircle, RefreshCw, CheckCircle2, XCircle } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { endpoints } from "@/lib/api";
import { PageHeader, Card, Btn, Loading, handleErr, fmtDateTime } from "@/pages/admin/AdminUI";
import { SmsUsage, AlertSettings } from "@/pages/admin/ApiMgmtExtras";
import { VimeoCard, LiveScoreCard } from "@/pages/admin/ProviderCards";

const Dot = ({ ok }) => <span className={`inline-block w-2 h-2 rounded-full ${ok ? "bg-emerald-400" : "bg-red-400"}`} />;

const Health = ({ h }) => !h ? <span className="text-[11px] text-slate-500">No traffic yet</span> : (
  <span className="text-[11px] text-slate-400">
    {h.lastOk && <span className="text-emerald-400">OK {fmtDateTime(h.lastOk)}</span>}
    {h.lastFail && <span className="text-red-400 ml-2">Fail {fmtDateTime(h.lastFail)}</span>}
    {h.lastFail && h.lastError && (!h.lastOk || h.lastFail > h.lastOk) && <span className="block text-red-300/80 truncate max-w-[260px]" title={h.lastError}>{h.lastError}</span>}
  </span>
);

const Row = ({ title, sub, checked, onChange, testid, configured = true, health, extra }) => (
  <div className="flex items-center justify-between gap-3 py-3 border-b border-[#273042] last:border-0">
    <div className="min-w-0">
      <div className="text-sm text-white font-semibold flex items-center gap-2">{title}
        {!configured && <span className="text-[10px] font-bold uppercase text-amber-400 bg-amber-400/10 px-1.5 py-0.5 rounded">keys missing</span>}
        {health?.down && <span className="text-[10px] font-bold uppercase text-white bg-red-600 px-1.5 py-0.5 rounded animate-pulse" data-testid={`${testid}-failing`}>failing</span>}
      </div>
      {sub && <div className="text-xs text-slate-400">{sub}</div>}
      {health !== undefined && <Health h={health} />}
    </div>
    <div className="flex items-center gap-3 shrink-0">
      {extra}
      <span className={`text-xs font-bold ${checked ? "text-emerald-400" : "text-slate-500"}`}>{checked ? "ON" : "OFF"}</span>
      <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} aria-label={`${title} ${checked ? "enabled" : "disabled"}`} />
    </div>
  </div>
);

const TestSms = ({ provider, sender }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    const phone = window.prompt(`Send a test SMS via ${sender} to which number? (leave empty = your admin number)`, "");
    if (phone === null) return;
    setBusy(true);
    try {
      const { data } = await endpoints.adminApiTestSms({ provider, phone: phone || undefined });
      if (data.ok) toast.success(`${sender} test SMS sent to ${data.phone}`);
      else toast.error(`${sender} SMS failed: ${data.detail || "not delivered"}`);
    } catch (e) { handleErr(e); } finally { setBusy(false); }
  };
  return (
    <button onClick={run} disabled={busy} data-testid={`test-sms-${provider}`}
      className="h-8 px-3 rounded-lg bg-white/5 border border-[#273042] text-xs text-white font-semibold inline-flex items-center gap-1.5 hover:bg-white/10 disabled:opacity-60">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Test
    </button>
  );
};

const CH = [["sms", "SMS"], ["whatsapp", "WhatsApp"], ["email", "Email"], ["push", "App push"]];
const QueuePanel = ({ queue, paused, onClear, onPause, busy, stops = {}, onStop, onStopAll }) => (
  <Card className="p-5 mb-6 border-red-500/30" data-testid="message-queue-panel">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <div className="text-white font-bold flex items-center gap-2"><OctagonX className="w-5 h-5 text-red-400" /> Message queue & emergency stop</div>
        <div className="text-xs text-slate-400 mt-1" data-testid="queue-count">
          {queue.length} job(s) currently sending · All sending is <b className={paused ? "text-red-400" : "text-emerald-400"}>{paused ? "PAUSED" : "ACTIVE"}</b> (OTP login codes are never paused)
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Btn variant="danger" onClick={onStopAll} disabled={busy} data-testid="queue-stop-all-btn"><OctagonX className="w-4 h-4" /> STOP ALL & delete queue</Btn>
        <Btn variant="danger" onClick={onClear} disabled={busy} data-testid="queue-clear-btn"><OctagonX className="w-4 h-4" /> Delete all queued messages</Btn>
        <Btn variant={paused ? "primary" : "ghost"} onClick={onPause} disabled={busy} data-testid="queue-pause-btn">
          {paused ? <><PlayCircle className="w-4 h-4" /> Resume sending</> : <><PauseCircle className="w-4 h-4" /> Pause all sending</>}
        </Btn>
      </div>
    </div>
    <div className="mt-4 flex flex-wrap gap-2" data-testid="channel-stops">
      {CH.map(([k, l]) => (
        <button key={k} onClick={() => onStop(k)} disabled={busy} data-testid={`channel-stop-${k}`} aria-pressed={!!stops[k]}
          className={`h-9 px-3 rounded-lg text-xs font-bold border ${stops[k] ? "border-red-500 bg-red-500/15 text-red-300" : "border-[#273042] bg-[#0A0C10] text-emerald-300"}`}>
          {l}: {stops[k] ? "STOPPED — tap to resume" : "ON — tap to stop"}
        </button>
      ))}
    </div>
    {queue.length > 0 && (
      <div className="mt-4 space-y-2">
        {queue.map((j) => (
          <div key={j.id} className="flex items-center justify-between text-xs bg-[#0A0C10] border border-[#273042] rounded-lg px-3 py-2" data-testid={`queue-job-${j.id}`}>
            <span className="text-slate-200 truncate">{j.kind} · {j.channel} · {j.preview}</span>
            <span className="text-slate-400 shrink-0 ml-3">{j.sent}/{j.total} sent · {fmtDateTime(j.createdAt)}</span>
          </div>
        ))}
      </div>
    )}
  </Card>
);

export default function AdminApiManagement() {
  const [d, setD] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, q] = await Promise.all([endpoints.adminApiMgmt(), endpoints.adminMessageQueue()]);
      setD(a.data); setCfg(a.data.config); setQueue(q.data.jobs || []);
    } catch (e) { handleErr(e); }
  }, []);
  useEffect(() => { load(); const t = setInterval(() => endpoints.adminMessageQueue().then((q) => setQueue(q.data.jobs || [])).catch(() => {}), 8000); return () => clearInterval(t); }, [load]);

  // Every toggle saves immediately so a broken API can be switched off at once.
  const save = async (next) => {
    const prev = cfg; setCfg(next);
    try { const { data } = await endpoints.adminApiMgmtSave(next); setCfg(data.config); toast.success(data.changes?.length ? `Saved: ${data.changes.join(", ")}` : "Saved"); }
    catch (e) { setCfg(prev); handleErr(e); }
  };
  const setSms = (svc, prov, v) => save({ ...cfg, messaging: { ...cfg.messaging, [svc]: { ...cfg.messaging[svc], sms: { ...cfg.messaging[svc].sms, [prov]: v } } } });
  const setCh = (svc, ch, v) => save({ ...cfg, messaging: { ...cfg.messaging, [svc]: { ...cfg.messaging[svc], [ch]: v } } });
  const setPay = (svc, m, v) => save({ ...cfg, payments: { ...cfg.payments, [svc]: { ...cfg.payments[svc], [m]: v } } });

  const clearQueue = async () => {
    if (!window.confirm("Stop and delete ALL queued messages now? Messages already handed to the SMS/WhatsApp provider cannot be recalled.")) return;
    setBusy(true);
    try { const { data } = await endpoints.adminMessageQueueClear({ delete: true }); toast.success(`${data.cancelled} stopped · ${data.deleted} deleted · ${data.pending} pending left`); load(); }
    catch (e) { handleErr(e); } finally { setBusy(false); }
  };
  const togglePause = () => save({ ...cfg, messagingPaused: !cfg.messagingPaused });
  const toggleStop = (k) => save({ ...cfg, channelStops: { ...(cfg.channelStops || {}), [k]: !(cfg.channelStops || {})[k] } });
  const stopAll = async () => {
    if (!window.confirm("Stop ALL SMS, WhatsApp, email and push sending and delete every queued message? (OTP login codes keep working)")) return;
    setBusy(true);
    try {
      const { data } = await endpoints.adminMessageQueueClear({ delete: true, pause: true });
      toast.success(`All sending stopped · ${data.deleted} queued deleted · ${data.pending} pending left`); load();
    } catch (e) { handleErr(e); } finally { setBusy(false); }
  };

  if (!d || !cfg) return <Loading />;
  const st = d.status, hl = d.health || {};
  const smsName = (p) => `${d.smsProviders[p].label} — Sender ID “${d.smsProviders[p].sender}”`;

  return (
    <div data-testid="admin-api-management">
      <PageHeader title="API Management" desc="Turn each SMS, WhatsApp and payment API on or off per service. Changes apply instantly — no fallback to APIs you switch off.">
        <Btn variant="ghost" onClick={load} data-testid="api-refresh-btn"><RefreshCw className="w-4 h-4" /> Refresh</Btn>
      </PageHeader>

      <QueuePanel queue={queue} paused={!!cfg.messagingPaused} onClear={clearQueue} onPause={togglePause} busy={busy} stops={cfg.channelStops || {}} onStop={toggleStop} onStopAll={stopAll} />

      <div className="grid lg:grid-cols-2 gap-6">
        {Object.entries(d.msgServices).map(([svc, label]) => (
          <Card key={svc} className="p-5" data-testid={`msg-service-${svc}`}>
            <div className="text-white font-bold flex items-center gap-2 mb-1"><MessageSquare className="w-5 h-5 text-[#E11D2A]" /> {label}</div>
            <div className="text-xs text-slate-400 mb-2">{svc === "otp" ? "Login & verification codes. Recommended: IZERE only." : "Reminders, receipts, broadcasts, tickets, try-again messages. Recommended: BEPAY only."}</div>
            {(cfg.messaging[svc].smsOrder || Object.keys(d.smsProviders)).map((p) => (
              <Row key={p} title={smsName(p)} sub="SMS" checked={cfg.messaging[svc].sms[p]} onChange={(v) => setSms(svc, p, v)}
                testid={`toggle-${svc}-sms-${p}`} configured={st[`sms_${p}`]?.configured} health={hl[`sms_${p}`]}
                extra={<TestSms provider={p} sender={d.smsProviders[p].sender} />} />
            ))}
            {svc === "otp" ? (
              <Row title="WhatsApp" sub="WhatsApp gateway (OTP codes)" checked={cfg.messaging.otp.whatsapp !== false} onChange={(v) => setCh("otp", "whatsapp", v)}
                testid="toggle-otp-whatsapp" configured={st.whatsapp?.configured} health={hl.whatsapp} />
            ) : (
              <>
                <Row title="WhatsApp — PDF payment receipts" sub="Only receipts after a successful payment" checked={cfg.messaging.receipt?.whatsapp !== false}
                  onChange={(v) => save({ ...cfg, messaging: { ...cfg.messaging, receipt: { whatsapp: v } } })}
                  testid="toggle-receipt-whatsapp" configured={st.whatsapp?.configured} />
                <div className="text-[11px] text-amber-300/90 py-2" data-testid="whatsapp-policy-note">WhatsApp is used ONLY for OTP codes and PDF receipts after successful payments. All other notifications go by SMS (BEPAY) and email.</div>
              </>
            )}
            <Row title="Email" sub="Gmail SMTP" checked={cfg.messaging[svc].email !== false} onChange={(v) => setCh(svc, "email", v)}
              testid={`toggle-${svc}-email`} configured={st.email?.configured} />
            {Object.values(cfg.messaging[svc].sms).every((x) => !x) && (
              <div className="mt-3 text-xs text-amber-400 flex items-center gap-1.5" data-testid={`warn-no-sms-${svc}`}><XCircle className="w-4 h-4" /> No SMS API is ON for this service — SMS will not be sent.</div>
            )}
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        {Object.entries(d.payServices).map(([svc, label]) => (
          <Card key={svc} className="p-5" data-testid={`pay-service-${svc}`}>
            <div className="text-white font-bold flex items-center gap-2 mb-2"><CreditCard className="w-5 h-5 text-[#E11D2A]" /> {label}</div>
            {Object.keys(cfg.payments[svc] || {}).map((m) => (
              <Row key={m} title={d.payProviders[m]} checked={cfg.payments[svc][m]} onChange={(v) => setPay(svc, m, v)}
                testid={`toggle-pay-${svc}-${m}`} configured={st[m]?.configured} health={hl[m]} />
            ))}
            {Object.values(cfg.payments[svc] || {}).every((x) => !x) && (
              <div className="mt-3 text-xs text-amber-400 flex items-center gap-1.5"><XCircle className="w-4 h-4" /> All methods are OFF — customers cannot pay for this service.</div>
            )}
          </Card>
        ))}
      </div>

      <VimeoCard cfg={cfg} onSave={save} providers={d.videoProviders} />
      <LiveScoreCard />
      <AlertSettings cfg={cfg} onSave={save} />
      <SmsUsage cfg={cfg} onSave={save} />

      <div className="mt-6 text-xs text-slate-500 flex items-center gap-1.5">
        <CheckCircle2 className="w-4 h-4 text-emerald-400" /> <Dot ok /> Every change is recorded in the Activity Log
        {d.updatedAt?.updatedAt && <> · last change {fmtDateTime(d.updatedAt.updatedAt)} by {d.updatedAt.updatedBy}</>}
      </div>
    </div>
  );
}
